// TRONA - Corporate Sign Up Page (shadcn/ui)
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { Button } from 'components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from 'components/ui/card';
import { Separator } from 'components/ui/separator';
import { Building2, Shield, CheckCircle2, Gift } from 'lucide-react';
import referralService from 'services/referralService';
import { LEGAL_CONFIG } from 'config/legal';

function SignUpDefault() {
  const { loginWithRedirect, isLoading } = useAuth0();
  const location = useLocation();
  const [referralInfo, setReferralInfo] = useState(null);
  const [termsAccepted, setTermsAccepted] = useState(false);

  // Capture referral code from URL on page load
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const refCode = params.get('ref');

    if (refCode) {
      // Store the code for use after Auth0 callback
      referralService.storeReferralCode(refCode);

      // Validate the code and show discount info
      referralService.validateCode(refCode).then((result) => {
        if (result.valid) {
          setReferralInfo(result);
        }
      });
    }
  }, [location]);

  const signUpWithGoogle = () => {
    // Store terms acceptance timestamp for legal compliance
    const termsAcceptance = {
      acceptedAt: new Date().toISOString(),
      termsVersion: LEGAL_CONFIG.documents.terms.version,
      privacyVersion: LEGAL_CONFIG.documents.privacy.version,
      userAgent: navigator.userAgent,
    };
    localStorage.setItem(
      'rely_terms_acceptance',
      JSON.stringify(termsAcceptance)
    );

    loginWithRedirect({
      connection: 'google-oauth2',
      screen_hint: 'signup',
    });
  };

  return (
    <div className="min-h-screen bg-[#fbfaff] dark:bg-neutral-950 dark:bg-[linear-gradient(180deg,#0d0f1a_0%,#151827_42%,#111827_100%)]">
      {/* Main Container */}
      <div className="flex min-h-screen">
        {/* Left Side - Branding */}
        <div className="relative hidden overflow-hidden bg-[#0d0f1a] lg:flex lg:w-1/2">
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute left-0 top-0 h-96 w-96 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white dark:bg-primary-400" />
            <div className="absolute bottom-0 right-0 h-[500px] w-[500px] translate-x-1/4 translate-y-1/4 rounded-full bg-white dark:bg-primary-400" />
          </div>

          {/* Content */}
          <div className="relative z-10 flex flex-col justify-between p-12 text-white">
            {/* Logo */}
            <div>
              <img
                src="/brand/full-logo.png"
                alt="TRONA"
                className="h-10 w-auto drop-shadow-[0_1px_16px_rgba(255,255,255,0.65)]"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = '/brand/icon.png';
                }}
              />
            </div>

            {/* Main Message */}
            <div className="max-w-md">
              <h1 className="mb-6 text-4xl font-bold leading-tight">
                Crea tu LLC en minutos
              </h1>
              <p className="mb-10 text-lg text-white/80">
                Comienza tu negocio en Estados Unidos de forma simple, segura y
                profesional.
              </p>

              {/* Features */}
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-white/20">
                    <Building2 className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="font-medium">Registro rápido</p>
                    <p className="text-sm text-white/70">Proceso 100% online</p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-white/20">
                    <Shield className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="font-medium">Datos protegidos</p>
                    <p className="text-sm text-white/70">
                      Encriptación de grado bancario
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-white/20">
                    <CheckCircle2 className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="font-medium">Soporte dedicado</p>
                    <p className="text-sm text-white/70">
                      Ayuda cuando la necesites
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="text-sm text-white/60">
              TRONA US - Todos los derechos reservados
            </div>
          </div>
        </div>

        {/* Right Side - Sign Up Form */}
        <div className="flex w-full items-center justify-center p-6 sm:p-12 lg:w-1/2">
          <div className="w-full max-w-[420px]">
            {/* Mobile Logo */}
            <div className="mb-8 flex justify-center lg:hidden">
              <img
                src="/brand/full-logo.png"
                alt="TRONA"
                className="h-10 w-auto"
              />
            </div>

            {/* Referral Discount Banner */}
            {referralInfo && (
              <div className="mb-4 rounded-lg border border-green-200 bg-green-50 p-4 dark:border-green-800 dark:bg-green-900/20">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-green-100 dark:bg-green-900">
                    <Gift className="h-5 w-5 text-green-600 dark:text-green-400" />
                  </div>
                  <div>
                    <p className="font-medium text-green-800 dark:text-green-200">
                      {referralInfo.discountDescription}
                    </p>
                    <p className="text-sm text-green-600 dark:text-green-400">
                      Código aplicado: {referralInfo.code}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Sign Up Card */}
            <Card className="border border-white/80 bg-white/88 shadow-[0_30px_90px_-58px_rgba(13,15,26,0.55)] backdrop-blur-xl dark:border-neutral-800/90 dark:bg-neutral-900/90 dark:shadow-black/40">
              <CardHeader className="space-y-1 pb-4">
                <CardTitle className="text-center text-2xl">
                  Crear cuenta
                </CardTitle>
                <CardDescription className="text-center">
                  {referralInfo
                    ? 'Regístrate y obtén tu descuento de referido'
                    : 'Regístrate para comenzar a crear tu LLC'}
                </CardDescription>
              </CardHeader>

              <CardContent className="space-y-6">
                {/* Terms Acceptance Checkbox */}
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="terms-checkbox"
                    checked={termsAccepted}
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                    className="mt-1 h-4 w-4 cursor-pointer rounded border-neutral-300 text-primary-500 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:border-neutral-600 dark:bg-neutral-800 dark:focus:ring-offset-neutral-900"
                  />
                  <label
                    htmlFor="terms-checkbox"
                    className="cursor-pointer text-sm text-neutral-600 dark:text-neutral-400"
                  >
                    He leído y acepto los{' '}
                    <a
                      href="/terms"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-medium text-primary-500 hover:text-primary-600 hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Términos de Servicio
                    </a>{' '}
                    y la{' '}
                    <a
                      href="/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-medium text-primary-500 hover:text-primary-600 hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Política de Privacidad
                    </a>
                  </label>
                </div>

                {/* Google Sign Up */}
                <Button
                  variant="outline"
                  size="lg"
                  className="h-12 w-full text-base font-medium"
                  onClick={signUpWithGoogle}
                  disabled={isLoading || !termsAccepted}
                  title={
                    !termsAccepted
                      ? 'Debes aceptar los términos y condiciones'
                      : ''
                  }
                >
                  <svg className="h-5 w-5" viewBox="0 0 24 24">
                    <path
                      fill="#4285F4"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="#34A853"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="#FBBC05"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="#EA4335"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                  {isLoading ? 'Cargando...' : 'Registrarse con Google'}
                </Button>

                {/* Divider */}
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <Separator />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-white px-3 text-neutral-400 dark:bg-neutral-900">
                      O
                    </span>
                  </div>
                </div>

                {/* Sign In Link */}
                <div className="space-y-4 text-center">
                  <p className="text-sm text-neutral-500 dark:text-neutral-400">
                    ¿Ya tienes una cuenta?
                  </p>
                  <Button
                    variant="secondary"
                    size="lg"
                    className="w-full"
                    onClick={() =>
                      (window.location.href = '/auth/sign-in/default')
                    }
                  >
                    Iniciar sesión
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Trust Badges - Mobile */}
            <div className="mt-8 flex justify-center gap-6 text-neutral-400 lg:hidden">
              <div className="flex items-center gap-2 text-xs">
                <Shield className="h-4 w-4" />
                <span>Seguro</span>
              </div>
              <div className="flex items-center gap-2 text-xs">
                <CheckCircle2 className="h-4 w-4" />
                <span>Verificado</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SignUpDefault;
